import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { CalendarDays, Info, Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

import { api, ErroApi } from '@/lib/api';
import { formatarData } from '@/lib/utils';
import type { Feriado, SimulacaoPrazo } from '@/features/notificacoes/tipos';
import { CabecalhoPagina } from '@/components/cabecalho-pagina';
import { Botao } from '@/components/ui/button';
import { Entrada } from '@/components/ui/input';
import { Campo } from '@/components/ui/campo';
import { Etiqueta } from '@/components/ui/badge';
import {
  Tabela,
  TabelaCabecalho,
  TabelaCelula,
  TabelaCorpo,
  TabelaLinha,
  TabelaTitulo,
  TabelaVazia,
} from '@/components/ui/table';
import { EsqueletoTabela } from '@/components/ui/skeleton';
import {
  Dialogo,
  DialogoCabecalho,
  DialogoConteudo,
  DialogoCorpo,
  DialogoDescricao,
  DialogoRodape,
  DialogoTitulo,
} from '@/components/ui/dialog';

const esquema = z.object({
  data: z.string().min(1, 'Informe a data.'),
  descricao: z.string().min(3, 'Descreva o feriado.'),
  municipio: z.string().optional(),
});

type Formulario = z.infer<typeof esquema>;

const COLUNAS = 4;

/** O dia civil vem do servidor como instante em UTC; recortar evita recuo de fuso. */
const diaCivil = (iso: string) => iso.slice(0, 10);

export function PaginaFeriados() {
  const queryClient = useQueryClient();
  const anoCorrente = new Date().getFullYear();
  const [ano, setAno] = React.useState(anoCorrente);
  const [dialogoAberto, setDialogoAberto] = React.useState(false);
  const [aRemover, setARemover] = React.useState<Feriado | null>(null);

  const { data: feriados, isPending } = useQuery({
    queryKey: ['feriados', ano],
    queryFn: () => api.get<Feriado[]>(`/feriados?ano=${ano}`),
  });

  const { data: simulacao } = useQuery({
    queryKey: ['feriados-simulacao'],
    queryFn: () => api.get<SimulacaoPrazo>('/feriados/simular-prazo'),
  });

  const formulario = useForm<Formulario>({
    resolver: zodResolver(esquema),
    defaultValues: { data: '', descricao: '', municipio: '' },
  });

  const invalidar = async () => {
    await queryClient.invalidateQueries({ queryKey: ['feriados'] });
    await queryClient.invalidateQueries({ queryKey: ['feriados-simulacao'] });
  };

  const criar = useMutation({
    mutationFn: (dados: Formulario) =>
      api.post<Feriado>('/feriados', { ...dados, municipio: dados.municipio || undefined }),
    onSuccess: async () => {
      toast.success('Feriado cadastrado.');
      setDialogoAberto(false);
      formulario.reset({ data: '', descricao: '', municipio: '' });
      await invalidar();
    },
    onError: (erro) =>
      toast.error(erro instanceof ErroApi ? erro.mensagemUsuario : 'Não foi possível cadastrar.'),
  });

  const remover = useMutation({
    mutationFn: (feriado: Feriado) => api.delete(`/feriados/${diaCivil(feriado.data)}`),
    onSuccess: async () => {
      toast.success('Feriado removido.');
      setARemover(null);
      await invalidar();
    },
    onError: (erro) =>
      toast.error(erro instanceof ErroApi ? erro.mensagemUsuario : 'Não foi possível remover.'),
  });

  const aoEnviar = formulario.handleSubmit((d) => criar.mutate(d));

  return (
    <div>
      <CabecalhoPagina
        titulo="Calendário de feriados"
        descricao="Usado para calcular o prazo de retirada da documentação informado ao paciente."
        acoes={
          <Botao onClick={() => setDialogoAberto(true)}>
            <Plus aria-hidden="true" />
            Novo feriado
          </Botao>
        }
      />

      <div className="border-status-enviado-borda bg-status-enviado-fundo text-status-enviado-texto mb-4 flex items-start gap-2 rounded-md border p-3 text-sm">
        <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        <div>
          <p className="font-medium">Cadastre os feriados móveis e os municipais.</p>
          <p className="mt-0.5">
            Os nacionais de data fixa já vêm carregados. Carnaval, Sexta-Feira Santa, Corpus Christi
            e o feriado do município mudam a cada ano — e é o municipal que costuma escapar,
            encurtando na prática o prazo do paciente.
          </p>
        </div>
      </div>

      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <label htmlFor="feriados-ano" className="text-sm font-medium">
            Ano
          </label>
          <Entrada
            id="feriados-ano"
            type="number"
            inputMode="numeric"
            className="w-28"
            value={ano}
            onChange={(evento) => {
              const valor = Number(evento.target.value);
              if (valor >= 2000 && valor <= 2100) setAno(valor);
            }}
          />
        </div>

        {simulacao && (
          <p className="text-silenciado-texto flex items-center gap-1.5 text-sm">
            <CalendarDays className="size-4 shrink-0" aria-hidden="true" />
            Um agendamento feito hoje daria prazo até{' '}
            <strong className="text-fundo-texto">{formatarData(simulacao.dataLimite)}</strong>
            <span>({simulacao.prazoDiasUteis} dias úteis)</span>
          </p>
        )}
      </div>

      <Tabela>
        <TabelaCabecalho>
          <TabelaLinha>
            <TabelaTitulo>Data</TabelaTitulo>
            <TabelaTitulo>Descrição</TabelaTitulo>
            <TabelaTitulo>Abrangência</TabelaTitulo>
            <TabelaTitulo className="text-right">Ações</TabelaTitulo>
          </TabelaLinha>
        </TabelaCabecalho>
        <TabelaCorpo>
          {isPending && <EsqueletoTabela colunas={COLUNAS} />}

          {!isPending && feriados?.length === 0 && (
            <TabelaVazia colunas={COLUNAS}>Nenhum feriado cadastrado em {ano}.</TabelaVazia>
          )}

          {feriados?.map((feriado) => (
            <TabelaLinha key={feriado.data}>
              <TabelaCelula rotulo="Data" className="tabular-nums">
                {formatarData(feriado.data)}
              </TabelaCelula>
              <TabelaCelula rotulo="Descrição" className="font-medium">
                {feriado.descricao}
              </TabelaCelula>
              <TabelaCelula rotulo="Abrangência">
                {feriado.municipio ? (
                  <Etiqueta tom="marca">{feriado.municipio}</Etiqueta>
                ) : (
                  <Etiqueta>Nacional</Etiqueta>
                )}
              </TabelaCelula>
              <TabelaCelula className="text-right">
                <Botao
                  variante="fantasma"
                  tamanho="sm"
                  onClick={() => setARemover(feriado)}
                  aria-label={`Remover ${feriado.descricao}`}
                >
                  <Trash2 aria-hidden="true" />
                  Remover
                </Botao>
              </TabelaCelula>
            </TabelaLinha>
          ))}
        </TabelaCorpo>
      </Tabela>

      <Dialogo open={dialogoAberto} onOpenChange={setDialogoAberto}>
        <DialogoConteudo>
          <DialogoCabecalho>
            <DialogoTitulo>Novo feriado</DialogoTitulo>
            <DialogoDescricao>
              A data deixa de contar como dia útil no cálculo do prazo de retirada.
            </DialogoDescricao>
          </DialogoCabecalho>

          <form onSubmit={(evento) => void aoEnviar(evento)} noValidate>
            <DialogoCorpo>
              <Campo
                id="feriado-data"
                rotulo="Data"
                obrigatorio
                erro={formulario.formState.errors.data?.message}
              >
                <Entrada type="date" autoFocus {...formulario.register('data')} />
              </Campo>

              <Campo
                id="feriado-descricao"
                rotulo="Descrição"
                obrigatorio
                erro={formulario.formState.errors.descricao?.message}
              >
                <Entrada placeholder="Carnaval" {...formulario.register('descricao')} />
              </Campo>

              <Campo
                id="feriado-municipio"
                rotulo="Município"
                ajuda="Deixe em branco para feriado nacional."
              >
                <Entrada {...formulario.register('municipio')} />
              </Campo>
            </DialogoCorpo>

            <DialogoRodape>
              <Botao type="button" variante="contorno" onClick={() => setDialogoAberto(false)}>
                Cancelar
              </Botao>
              <Botao type="submit" disabled={criar.isPending}>
                {criar.isPending && <Loader2 className="animate-spin" aria-hidden="true" />}
                Cadastrar
              </Botao>
            </DialogoRodape>
          </form>
        </DialogoConteudo>
      </Dialogo>

      <Dialogo open={aRemover !== null} onOpenChange={(aberto) => !aberto && setARemover(null)}>
        <DialogoConteudo>
          <DialogoCabecalho>
            <DialogoTitulo>Remover feriado</DialogoTitulo>
            <DialogoDescricao>
              {aRemover &&
                `${aRemover.descricao}, em ${formatarData(aRemover.data)}, voltará a contar como dia útil.`}
            </DialogoDescricao>
          </DialogoCabecalho>

          <DialogoRodape>
            <Botao type="button" variante="contorno" onClick={() => setARemover(null)}>
              Cancelar
            </Botao>
            <Botao
              type="button"
              variante="destrutivo"
              disabled={remover.isPending}
              onClick={() => aRemover && remover.mutate(aRemover)}
            >
              {remover.isPending && <Loader2 className="animate-spin" aria-hidden="true" />}
              Remover
            </Botao>
          </DialogoRodape>
        </DialogoConteudo>
      </Dialogo>
    </div>
  );
}
